import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Camera, Send, Settings, Flag, Info } from 'lucide-react-native';
import { router } from 'expo-router';
import { useTheme, SPACING, TYPOGRAPHY, ELEVATION } from '../../theme';
import { Button, ListItem } from '../common';

interface ActionsPanelProps {
  isClaimed: boolean;
  isOwnedByUser: boolean;
  structureId: string;
  isClaiming: boolean;
  onClaim: () => void;
  onUnclaim: () => void;
  onEditInsurance?: () => void;
  /** Shown as an owner-only row ("Notify my agent") only when passed. */
  onNotifyAgent?: () => void;
}

export function ActionsPanel({
  isClaimed,
  isOwnedByUser,
  structureId,
  isClaiming,
  onClaim,
  onUnclaim,
  onEditInsurance,
  onNotifyAgent,
}: ActionsPanelProps) {
  const { colors } = useTheme();

  // Shown to signed-out visitors too — the map sheet's claim button already
  // does this, and onClaim routes through the auth gate when there's no user.
  if (!isClaimed) {
    return (
      <View>
        <View style={ELEVATION.primaryGlow}>
          <Button
            title="Claim this structure"
            onPress={onClaim}
            variant="primary"
            size="lg"
            fullWidth
            leftIcon={Flag}
            loading={isClaiming}
            testID="structure-claim-button"
          />
        </View>
      </View>
    );
  }

  if (isOwnedByUser) {
    return (
      <View style={styles.container}>
        {/* Primary action. Working the checklist is the product's core loop,
            but its CTA is the readiness hero above — this panel only carries
            the secondary "share an observation" action and owner settings. */}
        <View style={[styles.actionGroup, { borderTopColor: colors.border }]}>
          <ListItem
            icon={Camera}
            label="Share an observation"
            onPress={() =>
              router.push({
                pathname: '/post/create',
                params: { structureId },
              })
            }
            showDivider={Boolean(onNotifyAgent)}
            style={styles.actionRow}
          />
          {onNotifyAgent && (
            <ListItem
              icon={Send}
              label="Notify my agent"
              onPress={onNotifyAgent}
              style={styles.actionRow}
              testID="structure-notify-agent"
            />
          )}
        </View>

        {/* Settings */}
        <View style={styles.settingsGroup}>
          {onEditInsurance && (
            <Pressable
              style={styles.settingsRow}
              onPress={onEditInsurance}
              testID="structure-edit-insurance">
              <Settings size={14} color={colors.textTertiary} />
              <Text style={[styles.settingsLabel, { color: colors.textSecondary }]}>
                Change preparation model or stakeholder type
              </Text>
            </Pressable>
          )}
          <Pressable
            testID="structure-release-claim"
            style={styles.settingsRow}
            onPress={onUnclaim}
            disabled={isClaiming}>
            <Text
              style={[
                styles.settingsLabel,
                { color: colors.danger, opacity: isClaiming ? 0.5 : 1 },
              ]}>
              Release claim
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (isClaimed && !isOwnedByUser) {
    return (
      <View style={[styles.infoBlock, { borderTopColor: colors.border }]}>
        <Info size={16} color={colors.textSecondary} style={styles.infoIcon} />
        <Text style={[styles.infoText, { color: colors.textSecondary }]}>
          This structure is claimed by another resident. You can still view and add observations.
        </Text>
      </View>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  container: {
    gap: SPACING.md,
  },
  // Hairline row, not a filled panel (mockup `.rowlink`).
  actionGroup: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  actionRow: {
    paddingHorizontal: 0,
  },
  settingsGroup: {
    gap: SPACING.sm,
  },
  settingsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.xs,
  },
  settingsLabel: {
    ...TYPOGRAPHY.bodySmall,
  },
  infoBlock: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  infoIcon: {
    marginTop: 2,
  },
  infoText: {
    ...TYPOGRAPHY.bodySmall,
    flex: 1,
    lineHeight: 19,
    marginLeft: SPACING.sm,
  },
});
